import {
	keepPreviousData,
	useInfiniteQuery,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Barcode, Eye } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";
import {
	pickDefaultScope,
	ScopePicker,
	type ScopeRef,
	scopeLabel,
	useScopePersistedState,
	useScopeTree,
} from "@/components/scope-picker";
import type { ColumnDef, ColumnFiltersState, SortingState } from "@/components/ui/data-table";
import { DataTable, DataTableColumnHeader } from "@/components/ui/data-table";
import { MobileFilterField, MobileFilters } from "@/components/ui/mobile-filters";
import { PageHeader } from "@/components/ui/page-header";
import { useAuth } from "@/features/auth/use-auth";
import {
	columnFiltersToSearch,
	type ProductSearchParams,
	searchToColumnFilters,
} from "@/features/products/column-filters-to-search";
import {
	buildCategoryTree,
	CategoryFilterCombobox,
} from "@/features/products/components/category-filter-combobox";
import { ProductDetailPanel } from "@/features/products/components/product-detail-panel";
import { ProductSearchBar } from "@/features/products/components/product-search-bar";
import { productsApi } from "@/features/products/products-api";
import type { ProductItem } from "@/features/products/types";
import { useTenantSwitcher } from "@/features/tenant-switcher";
import { useIsBelowMd } from "@/hooks/use-mobile";
import { apiFetch } from "@/lib/auth-api";
import { RODZAJ_OPTIONS } from "@/lib/rodzaj-leku";
import { ensureElementVisible } from "@/lib/route-guards";
import { decodeScope, encodeScope } from "@/lib/scope";
import { cn } from "@/lib/utils";

interface CategoryTreeNode {
	code: string;
	label: string;
	parentCode: string | null;
	level: number;
	sortOrder: number;
}

export const searchSchema = z.object({
	q: z.string().optional(),
	nameLike: z.string().optional(),
	blozPrefix: z.string().optional(),
	eanPrefix: z.string().optional(),
	producerLike: z.string().optional(),
	category: z.string().optional(),
	postc: z.string().optional(),
	rodzaj: z.array(z.string()).optional(),
	dawkaOp: z.string().optional(),
	dawkaVal: z.number().optional(),
	dawkaMax: z.number().optional(),
	opakOp: z.string().optional(),
	opakVal: z.number().optional(),
	opakMax: z.number().optional(),
	stockOp: z.string().optional(),
	stockVal: z.number().optional(),
	stockMax: z.number().optional(),
	rotationOp: z.string().optional(),
	rotationVal: z.number().optional(),
	rotationMax: z.number().optional(),
	sortBy: z.string().optional(),
	sortDir: z.enum(["asc", "desc"]).optional(),
	scope: z.string().optional(),
	available: z.boolean().optional(),
	// Deep-link from external panels (np. Strateg Cenowy panel boczny "Karta →").
	// TanStack Router default parser auto-koerc-uje numeryczne search-paramy do number,
	// wiec akceptujemy obydwa typy i normalizujemy do stringa (bloz jest semantycznie kodem).
	bloz: z
		.union([z.string(), z.number()])
		.transform((v) => String(v))
		.optional(),
});

type TowarySearch = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/_auth/kartoteka/towary")({
	beforeLoad: async ({ context, location }) => {
		await ensureElementVisible(context.queryClient, "sidebar.kartoteka.towary", location.pathname);
	},
	validateSearch: (s: Record<string, unknown>): TowarySearch => searchSchema.parse(s),
	component: TowaryListPage,
});

const PAGE_SIZE = 500;
const EXPORT_PAGE_SIZE = 500;

function TowaryListPage() {
	const queryClient = useQueryClient();
	const { selectedTenantId } = useTenantSwitcher();
	const { user } = useAuth();
	const search = Route.useSearch();
	const navigate = Route.useNavigate();
	const isBelowMd = useIsBelowMd();

	const [selectedKod07, setSelectedKod07] = useState<string | null>(null);

	// Deep-link auto-open: jesli URL ma ?bloz=NNNNNNN (np. z panelu Strateg Cenowy),
	// otworz detail panel po zmianie URL. Po otworzeniu nie czyscimy URL — uzytkownik
	// moze odswiezyc/share'owac link. Re-fire przy kazdej zmianie ?bloz= (deep-link
	// na inny produkt z tego samego widoku).
	useEffect(() => {
		if (search.bloz) {
			setSelectedKod07(search.bloz);
		}
	}, [search.bloz]);

	const showAvailable = search.available === true;

	// Scope sync: URL search param > localStorage > default { type: "wlasne" }.
	// Mount: bierzemy URL ?scope= jezeli jest, fallback do localStorage, fallback
	// do default. Pick z ScopePickera updatuje URL (replace) i localStorage.
	const persistKey = user?.id ? `scope:towary:${user.id}` : "scope:towary:anon";
	const [persistedScope, setPersistedScope] = useScopePersistedState(persistKey, {
		type: "wlasne",
	});
	const initialScope: ScopeRef =
		(search.scope ? decodeScope(search.scope) : null) ?? persistedScope;
	const [scope, setScopeRaw] = useState<ScopeRef>(initialScope);
	const setScope = useCallback(
		(s: ScopeRef) => {
			setScopeRaw(s);
			setPersistedScope(s);
			navigate({
				search: (prev: TowarySearch) => ({ ...prev, scope: encodeScope(s) }),
				replace: true,
			});
		},
		[navigate, setPersistedScope],
	);
	const encodedScope = encodeScope(scope);

	// Auto-fallback gdy aktualny scope branch jest pusty (np. konto z dostepem
	// tylko do franczyzy zaladowane z domyslnym scope=wlasne dostalo by 0
	// wynikow). Mirror logiki z product-report-page.tsx.
	const { data: scopeTree } = useScopeTree();
	const scopeFallbackApplied = useRef(false);
	useEffect(() => {
		if (!scopeTree || scopeFallbackApplied.current) return;
		const hasW = scopeTree.wlasne.idfirms.length > 0;
		const hasF = scopeTree.franczyzaIdfirms.length > 0;
		if (scope.type === "wlasne" && !hasW && hasF) {
			scopeFallbackApplied.current = true;
			const safe = pickDefaultScope(scopeTree);
			if (safe) setScope(safe);
		} else if (scope.type === "franczyza" && !hasF && hasW) {
			scopeFallbackApplied.current = true;
			setScope({ type: "wlasne" });
		}
	}, [scopeTree, scope.type, setScope]);

	// Konto bez zadnych aptek (super_admin bez visibility / brak danych) — pokaz
	// pusty stan zamiast pulapki listy. scopeTree musi zaladowac sie raz, zeby
	// odroznic "loading" od "no pharmacies at all".
	const hasAnyPharmacies =
		scopeTree === undefined ||
		scopeTree.wlasne.idfirms.length > 0 ||
		scopeTree.franczyzaIdfirms.length > 0;

	// Category tree for the top-bar quick picker (restores pre-refactor UX).
	const { data: categoryTree } = useQuery({
		queryKey: ["categories", "tree"],
		queryFn: () => apiFetch<CategoryTreeNode[]>("/categories/tree"),
		staleTime: 5 * 60_000,
	});
	const categoryOptions = useMemo(
		() => (categoryTree ? buildCategoryTree(categoryTree) : []),
		[categoryTree],
	);

	const setCategory = useCallback(
		(code: string) => {
			navigate({
				search: (prev) => ({ ...prev, category: code || undefined }),
				replace: true,
			});
		},
		[navigate],
	);

	// Column filters UI state, initialized from URL on mount.
	// Subsequent URL changes from other sources (e.g. back/forward) also reflect.
	const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(() =>
		searchToColumnFilters(search),
	);
	const [sorting, setSorting] = useState<SortingState>(() =>
		search.sortBy
			? [{ id: search.sortBy, desc: search.sortDir === "desc" }]
			: [
					{
						id: search.available ? "totalStockAvailable" : "totalStock",
						desc: true,
					},
				],
	);

	// Toggling the checkbox swaps the visible "Stan" column. We also swap the
	// active sort if it currently matches the OTHER column — otherwise the
	// header would say "Stan dostepny" but rows would still be ordered by
	// physical stock (or vice versa). Only triggers when the user is sorting
	// by stock; other sort columns stay put.
	const setShowAvailable = useCallback(
		(v: boolean) => {
			const oldKey = v ? "totalStock" : "totalStockAvailable";
			const newKey = v ? "totalStockAvailable" : "totalStock";
			navigate({
				search: (prev) => ({
					...prev,
					available: v ? true : undefined,
					sortBy: prev.sortBy === oldKey ? newKey : prev.sortBy,
				}),
				replace: true,
			});
			setSorting((s) => s.map((x) => (x.id === oldKey ? { ...x, id: newKey } : x)));
		},
		[navigate],
	);

	// Push columnFilters → URL (replace, keep q + sort).
	// didMountFiltersRef skips the first invocation: state was already hydrated
	// from URL on mount, so re-emitting it just adds a redundant history entry.
	const didMountFiltersRef = useRef(false);
	useEffect(() => {
		if (!didMountFiltersRef.current) {
			didMountFiltersRef.current = true;
			return;
		}
		const patch = columnFiltersToSearch(columnFilters);
		navigate({
			search: (prev: TowarySearch) => {
				// Preserve fields owned by other UI (top-bar combobox, search bar, sort header)
				// — the column-filter effect should only overwrite column-filter-derived keys.
				const next: TowarySearch = {
					q: prev.q,
					category: prev.category,
					scope: prev.scope,
					available: prev.available,
					sortBy: prev.sortBy,
					sortDir: prev.sortDir,
					...patch,
				};
				return next;
			},
			replace: true,
		});
	}, [columnFilters, navigate]);

	// Push sorting → URL. Same mount-skip pattern as filters.
	const didMountSortingRef = useRef(false);
	useEffect(() => {
		if (!didMountSortingRef.current) {
			didMountSortingRef.current = true;
			return;
		}
		const s = sorting[0];
		navigate({
			search: (prev: TowarySearch) => ({
				...prev,
				sortBy: s?.id,
				sortDir: s ? (s.desc ? "desc" : "asc") : undefined,
			}),
			replace: true,
		});
	}, [sorting, navigate]);

	const setQuery = useCallback(
		(q: string) => {
			navigate({
				search: (prev: TowarySearch) => ({ ...prev, q: q || undefined }),
				replace: true,
			});
		},
		[navigate],
	);

	const handleRowHover = useCallback(
		(row: ProductItem) => {
			queryClient.prefetchQuery({
				queryKey: ["product-detail", row.bloz07, `scope:${encodedScope}`],
				queryFn: () => productsApi.getDetail(row.bloz07, { scope: encodedScope }),
				staleTime: 60_000,
			});
		},
		[queryClient, encodedScope],
	);

	const columns = useMemo<ColumnDef<ProductItem>[]>(
		() => [
			{
				accessorKey: "nazwa",
				header: ({ column }) => <DataTableColumnHeader column={column} title="Nazwa" />,
				cell: ({ row }) => (
					<button
						type="button"
						className="font-medium truncate text-left w-full hover:text-primary"
						onMouseEnter={() => handleRowHover(row.original)}
						onClick={(e) => {
							e.stopPropagation();
							setSelectedKod07(row.original.bloz07);
						}}
					>
						{row.original.nazwa}
					</button>
				),
				size: 240,
			},
			{
				accessorKey: "bloz07",
				header: ({ column }) => <DataTableColumnHeader column={column} title="BLOZ" />,
				cell: ({ row }) => (
					<span className="font-mono font-bold text-primary">{row.original.bloz07}</span>
				),
				size: 110,
				meta: { minChars: 2 },
			},
			{
				accessorKey: "kodean",
				header: ({ column }) => <DataTableColumnHeader column={column} title="EAN" />,
				cell: ({ row }) => (
					<div className="flex items-center gap-1.5 text-muted-foreground">
						<Barcode className="size-3 opacity-50" />
						<span className="font-mono">{row.original.kodean || "—"}</span>
					</div>
				),
				size: 140,
				meta: { minChars: 3 },
			},
			{
				accessorKey: "prodc",
				header: ({ column }) => <DataTableColumnHeader column={column} title="Producent" />,
				cell: ({ row }) => <span>{row.original.prodc || "—"}</span>,
				size: 180,
			},
			{
				accessorKey: "primaryCategoryCode",
				header: ({ column }) => <DataTableColumnHeader column={column} title="Kategoria" />,
				cell: ({ row }) =>
					row.original.primaryCategoryCode ? (
						<span className="text-xs">
							<span className="font-mono text-muted-foreground mr-1">
								{row.original.primaryCategoryCode}
							</span>
							{row.original.primaryCategoryLabel}
						</span>
					) : null,
				size: 200,
				meta: { minChars: 2 },
			},
			{
				accessorKey: "postc",
				header: ({ column }) => <DataTableColumnHeader column={column} title="Postać" />,
				cell: ({ row }) => <span>{row.original.postc || "—"}</span>,
				size: 120,
			},
			{
				accessorKey: "dawka",
				header: ({ column }) => <DataTableColumnHeader column={column} title="Dawka" />,
				cell: ({ row }) => <span>{row.original.dawka || "—"}</span>,
				size: 120,
				// Dawka/opakowanie to tekst wyrownany do prawej, nie miara — na telefonie
				// tylko w szczegolach wiersza.
				meta: { isNumeric: true, mobileRole: "detail" },
			},
			{
				accessorKey: "opakh",
				header: ({ column }) => <DataTableColumnHeader column={column} title="Opakowanie" />,
				cell: ({ row }) => <span>{row.original.opakh || "—"}</span>,
				size: 140,
				meta: { isNumeric: true, mobileRole: "detail" },
			},
			{
				accessorKey: "rodzaj",
				header: ({ column }) => <DataTableColumnHeader column={column} title="Rodzaj" />,
				cell: ({ row }) => {
					const rodzaj = row.original.rodzaj;
					if (!rodzaj || rodzaj === "BD") {
						return <span className="text-muted-foreground">{"—"}</span>;
					}
					const color =
						rodzaj === "OTC"
							? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/25"
							: rodzaj === "RX100"
								? "bg-violet-500/15 text-violet-700 dark:text-violet-400 border-violet-500/25"
								: "bg-blue-500/15 text-blue-700 dark:text-blue-400 border-blue-500/25";
					return (
						<span
							className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium border ${color}`}
						>
							{rodzaj}
						</span>
					);
				},
				size: 100,
				meta: { facetedOptions: RODZAJ_OPTIONS },
			},
			showAvailable
				? ({
						accessorKey: "totalStockAvailable",
						header: ({ column }) => <DataTableColumnHeader column={column} title="Stan dostepny" />,
						cell: ({ row }) =>
							row.original.totalStockAvailable != null && row.original.totalStockAvailable > 0 ? (
								<span className="font-medium tabular-nums">
									{Math.round(row.original.totalStockAvailable)}
								</span>
							) : (
								<span className="text-muted-foreground">{"—"}</span>
							),
						size: 110,
						meta: { isNumeric: true, mobileDefaultMeasure: true },
					} satisfies ColumnDef<ProductItem>)
				: ({
						accessorKey: "totalStock",
						header: ({ column }) => <DataTableColumnHeader column={column} title="Stan" />,
						cell: ({ row }) =>
							row.original.totalStock != null && row.original.totalStock > 0 ? (
								<span className="font-medium tabular-nums">
									{Math.round(row.original.totalStock)}
								</span>
							) : (
								<span className="text-muted-foreground">{"—"}</span>
							),
						size: 100,
						meta: { isNumeric: true, mobileDefaultMeasure: true },
					} satisfies ColumnDef<ProductItem>),
			{
				accessorKey: "rotationDays",
				header: ({ column }) => <DataTableColumnHeader column={column} title="Rotacja" />,
				cell: ({ row }) => {
					if (row.original.rotationDays == null) {
						return <span className="text-muted-foreground">{"—"}</span>;
					}
					const days = Math.round(row.original.rotationDays);
					let colorClass = "text-foreground";
					if (days < 7) colorClass = "text-red-600 dark:text-red-400";
					else if (days < 14) colorClass = "text-amber-600 dark:text-amber-400";
					else if (days > 30) colorClass = "text-emerald-600 dark:text-emerald-400";
					return <span className={`font-medium tabular-nums ${colorClass}`}>{days} dni</span>;
				},
				size: 110,
				meta: { isNumeric: true },
			},
			{
				id: "actions",
				header: "",
				size: 52,
				enableSorting: false,
				enableColumnFilter: false,
				meta: { alwaysLast: true },
				cell: ({ row }) => (
					<button
						type="button"
						onClick={(e) => {
							e.stopPropagation();
							setSelectedKod07(row.original.bloz07);
						}}
						className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors max-md:flex max-md:size-9 max-md:items-center max-md:justify-center max-md:border max-md:border-border"
						title="Szczegóły produktu"
						aria-label="Szczegóły produktu"
					>
						<Eye className="size-3.5" />
					</button>
				),
			},
		],
		[handleRowHover, showAvailable],
	);
	const fetchAllForExport = useCallback(async (): Promise<Record<string, unknown>[]> => {
		const rows: Record<string, unknown>[] = [];
		let offset = 0;
		let total = Number.POSITIVE_INFINITY;

		while (offset < total) {
			const response = await productsApi.search({
				...(search as ProductSearchParams),
				scope: encodedScope,
				limit: EXPORT_PAGE_SIZE,
				offset,
			});
			for (const item of response.items) rows.push({ ...item });
			total = response.total;
			if (response.items.length === 0) break;
			offset += response.items.length;
		}

		return rows;
	}, [search, encodedScope]);

	const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching, isPending } =
		useInfiniteQuery({
			queryKey: ["products", "search", selectedTenantId, search, encodedScope],
			queryFn: ({ pageParam }) =>
				productsApi.search({
					...(search as ProductSearchParams),
					scope: encodedScope,
					limit: PAGE_SIZE,
					offset: pageParam as number,
				}),
			initialPageParam: 0,
			getNextPageParam: (lastPage, allPages) => {
				const items = lastPage.items ?? [];
				const total = lastPage.total ?? 0;
				const loaded = allPages.reduce((sum, p) => sum + (p.items ?? []).length, 0);
				if (items.length < PAGE_SIZE || total === 0) return undefined;
				return loaded < total ? loaded : undefined;
			},
			placeholderData: keepPreviousData,
			enabled: hasAnyPharmacies,
			// Back/forward navigation nie powinien refetchowac calej infinite query —
			// MVs za endpointem /products/search refresh hourly, 2 min staleTime jest
			// bezpiecznie nizej niz refresh cycle.
			staleTime: 2 * 60_000,
			gcTime: 10 * 60_000,
		});

	const products = useMemo(() => data?.pages.flatMap((p) => p.items ?? []) ?? [], [data]);
	const totalCount = data?.pages[0]?.total;
	// Spinner overlay przy initial load + refetchu (np. switch wlasna/franczyza,
	// zmiana filtra). keepPreviousData trzyma stare wiersze, bez overlaya user
	// nie widzi ze trwa fetch — dane "wskakuja" po sekundzie.
	const showTableLoader = (isFetching || isPending) && !isFetchingNextPage;

	// Telefon: wyszukiwarka zostaje nad lista, reszta filtrow zwija sie do chipow
	// (`MobileFilters`). Kontrolki budowane raz — ten sam JSX w rzedzie desktopu
	// i w arkuszu, zeby nie bylo drugiej implementacji filtrow.
	const stockToggle = (mobile: boolean) => (
		<div className="flex items-center rounded-md border border-border overflow-hidden">
			{[
				{ label: "Cały", value: false, title: "Cały stan magazynowy" },
				{
					label: "Dostępny",
					value: true,
					title: "Stan bez partii wstrzymanych do sprzedaży",
				},
			].map((option) => (
				<button
					key={option.label}
					type="button"
					title={option.title}
					className={cn(
						"px-2 py-1 font-medium transition-colors",
						mobile && "h-10 flex-1 text-sm",
						showAvailable === option.value
							? "bg-primary text-primary-foreground"
							: "bg-background text-muted-foreground hover:text-foreground hover:bg-muted/60",
					)}
					onClick={() => setShowAvailable(option.value)}
				>
					{option.label}
				</button>
			))}
		</div>
	);
	const categoryPicker = (className: string) => (
		<CategoryFilterCombobox
			options={categoryOptions}
			value={search.category ?? ""}
			onChange={setCategory}
			className={className}
		/>
	);
	const mobileScope = (
		<ScopePicker
			value={scope}
			onChange={setScope}
			persistKey={persistKey}
			className="h-10 w-full"
		/>
	);
	// „Wyczyść” w arkuszu: domyslny zakres (Wlasne), caly stan, wszystkie kategorie.
	// Jedna nawigacja zamiast trzech setterow — kolejne `navigate` z updaterem
	// moglyby czytac jeszcze stary URL i nadpisac sobie zmiany.
	const clearMobileFilters = useCallback(() => {
		// Konto bez aptek wlasnych (franczyzobiorca): ten sam bezpieczny domyslny
		// zakres co auto-fallback wyzej, zeby „Wyczyść” nie dawalo pustej listy.
		const wlasne: ScopeRef = { type: "wlasne" };
		const def: ScopeRef =
			scopeTree && scopeTree.wlasne.idfirms.length === 0
				? (pickDefaultScope(scopeTree) ?? wlasne)
				: wlasne;
		setScopeRaw(def);
		setPersistedScope(def);
		setSorting((s) =>
			s.map((x) => (x.id === "totalStockAvailable" ? { ...x, id: "totalStock" } : x)),
		);
		navigate({
			search: (prev: TowarySearch) => ({
				...prev,
				scope: encodeScope(def),
				available: undefined,
				category: undefined,
				sortBy: prev.sortBy === "totalStockAvailable" ? "totalStock" : prev.sortBy,
			}),
			replace: true,
		});
	}, [navigate, setPersistedScope, scopeTree]);
	const selectedCategoryLabel = search.category
		? (categoryOptions.find((o) => o.value === search.category)?.label ?? search.category)
		: "Wszystkie kategorie";

	return (
		<div className="flex flex-col h-full overflow-hidden">
			<div className="p-6 pb-2 max-md:px-3 max-md:pt-3 max-md:pb-1 max-md:empty:hidden">
				<PageHeader
					title="Kartoteka Towarow"
					description="Produkty z kartoteki aptecznej z mapowaniem kodow APC"
				/>
			</div>

			{isBelowMd && (
				<div className="px-3 pt-2 pb-2 border-t bg-card/50">
					<ProductSearchBar value={search.q ?? ""} onChange={setQuery} />
				</div>
			)}
			<MobileFilters
				chips={[
					{
						id: "scope",
						label: "Zakres aptek",
						value: scopeLabel(scope, scopeTree),
						active: scope.type !== "wlasne",
						picker: mobileScope,
					},
					{
						id: "stock",
						label: "Stan",
						value: showAvailable ? "Stan dostępny" : "Stan cały",
						active: showAvailable,
						picker: stockToggle(true),
					},
					{
						id: "category",
						label: "Kategoria",
						value: selectedCategoryLabel,
						active: !!search.category,
						picker: categoryPicker("h-10 w-full"),
					},
				]}
				sheetContent={
					<>
						<MobileFilterField label="Zakres aptek">{mobileScope}</MobileFilterField>
						<MobileFilterField label="Stan">{stockToggle(true)}</MobileFilterField>
						<MobileFilterField label="Kategoria">{categoryPicker("h-10 w-full")}</MobileFilterField>
					</>
				}
				onClear={clearMobileFilters}
				resultLabel={
					totalCount != null
						? `Pokaż ${totalCount.toLocaleString("pl-PL")} produktów`
						: "Pokaż produkty"
				}
			>
				<div className="px-6 py-3 border-y bg-card/50 flex items-center gap-3">
					<div className="flex-1">
						<ProductSearchBar value={search.q ?? ""} onChange={setQuery} />
					</div>
					<ScopePicker value={scope} onChange={setScope} persistKey={persistKey} />
					<div className="flex items-center gap-1.5 text-xs whitespace-nowrap">
						<span className="text-muted-foreground">Stan:</span>
						{stockToggle(false)}
					</div>
					{categoryPicker("w-64 h-10")}
				</div>
			</MobileFilters>

			<div className="flex-1 min-h-0 p-6 pt-3 max-md:p-0">
				{!hasAnyPharmacies ? (
					<div className="h-full rounded-md border border-border bg-card flex items-center justify-center p-6 text-sm text-muted-foreground">
						Brak przypisanych aptek dla Twojego konta. Skontaktuj się z administratorem, żeby
						uzyskać dostęp do kartoteki towarów.
					</div>
				) : (
					<div className="relative h-full">
						<DataTable
							tableId="towary-kartoteka"
							data={products}
							columns={columns}
							serverFiltering={true}
							columnFilters={columnFilters}
							onColumnFiltersChange={setColumnFilters}
							sorting={sorting}
							onSortingChange={setSorting}
							totalCount={totalCount}
							onFetchMore={fetchNextPage}
							hasNextPage={hasNextPage}
							isFetchingNextPage={isFetchingNextPage}
							isLoading={showTableLoader}
							getRowId={(row) => row.bloz07}
							onRowCtrlClick={(rowId) => setSelectedKod07(rowId)}
							className="h-full"
							enableToolbar
							enableColumnVisibility
							enableSchemes
							enableColumnPinning
							defaultPinnedColumns={["nazwa"]}
							enableExport
							exportFileName={`kartoteka-towary-${scope.type}`}
							fetchAllForExport={fetchAllForExport}
							onExportError={(err) => console.error("[Towary Export]", err)}
							alwaysExportColumns={["nazwa", "bloz07", "kodean"]}
							columnLabels={{
								nazwa: "Nazwa",
								bloz07: "BLOZ",
								kodean: "EAN",
								prodc: "Producent",
								primaryCategoryCode: "Kategoria",
								postc: "Postać",
								dawka: "Dawka",
								opakh: "Opakowanie",
								rodzaj: "Rodzaj",
								totalStock: "Stan cały",
								totalStockAvailable: "Stan dostępny",
								stockPharmacyCount: "Liczba aptek ze stanem",
								totalSold90d: "Sprzedaż 90 dni",
								rotationDays: "Rotacja (dni)",
							}}
						/>
					</div>
				)}
			</div>

			<ProductDetailPanel
				kod07={selectedKod07}
				scope={scope}
				onClose={() => setSelectedKod07(null)}
			/>
		</div>
	);
}
